import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BUSINESS } from "../constants/business";

const SECTIONS = [
  ["Diagnostics", [
    "Every diagnostic carries a cost for the time, knowledge and equipment used to determine the cause of a fault.",
    "Payment for diagnostics covers the evaluation service performed and does not guarantee that the customer will authorize or proceed with the repair at Ascencio Auto Repair.",
    "Electrical, intermittent or highly complex faults may require additional diagnostic hours. Any additional work that generates significant charges will be communicated to the customer before continuing.",
  ]],
  ["Repair Authorization", [
    "No significant additional repairs will be performed without the customer's authorization.",
    "Authorization may be given in writing, by text message, email, signature, electronic system, or another communication method agreed upon with the customer.",
    "By authorizing work, the customer accepts the corresponding costs of parts, labor and other authorized services.",
  ]],
  ["Estimates", [
    "Estimates are made based on the information available at the time of inspection.",
    "During disassembly or repair, additional damage or problems that were not initially visible may be discovered.",
    "In these cases, Ascencio Auto Repair will inform the customer before performing additional work that represents a significant change to the authorized cost.",
  ]],
  ["Customer-Supplied Parts", [
    "Ascencio Auto Repair may accept customer-provided parts depending on the type of repair.",
    "Customer-provided parts carry no warranty from Ascencio Auto Repair.",
    "If a customer-provided part is incorrect, defective, or fails after installation, any additional diagnosis, disassembly, reinstallation or labor will be the customer's responsibility.",
  ]],
  ["Warranty", [
    "Warranty conditions depend on the repair and the parts used.",
    "The applicable warranty will be stated on the invoice, repair order, or corresponding documentation.",
    "A warranty does not automatically cover problems different from or unrelated to the repair originally performed.",
    "Any potential warranty claim must be inspected and evaluated by Ascencio Auto Repair before determining whether it is covered.",
  ]],
  ["Repair Timeframes", [
    "Completion dates and times provided are estimates.",
    "Times may change due to parts availability, additional diagnostics, supplier delays, specialized work, problems found during disassembly, or other circumstances beyond the shop's reasonable control.",
    "Ascencio Auto Repair will make reasonable efforts to keep the customer informed when there is a significant change.",
  ]],
  ["Vehicles Coming from Other Shops", [
    "Ascencio Auto Repair may accept vehicles previously diagnosed, disassembled, or repaired by another person or shop.",
    "However, we may need to perform our own diagnosis before recommending or guaranteeing any repair.",
    "We are not responsible for work, damage, modifications, or diagnoses previously performed by third parties.",
  ]],
  ["Modified Vehicles", [
    "The customer must report any relevant modifications made to the vehicle.",
    "Ascencio Auto Repair will not be responsible for problems caused by modifications, installations, or repairs previously performed by third parties.",
  ]],
  ["Personal Belongings", [
    "The customer is responsible for removing money, tools, documents, and other valuables before leaving the vehicle.",
    "Ascencio Auto Repair is not responsible for personal items left inside the vehicle, except where applicable law states otherwise.",
  ]],
  ["Completed Vehicles & Storage", [
    "Once the customer has been notified that the vehicle is finished or ready for pickup, the customer must make the corresponding payment and pick it up within the established period.",
    "Vehicles that remain on the premises after the permitted period may incur storage charges, according to the shop's current policies and applicable law.",
    "Any applicable storage charge will be communicated to the customer.",
  ]],
  ["Unclaimed Vehicles", [
    "Vehicles that remain uncollected, or whose owners do not respond to our reasonable communication attempts, may be subject to the procedures permitted by applicable Oklahoma law.",
    "Ascencio Auto Repair will comply with all required legal notices and procedures before exercising any available right regarding an unclaimed vehicle.",
  ]],
  ["Payment", [
    "The corresponding payment must be made according to the conditions established in the repair order or invoice.",
    "Unless previously agreed, the balance must be paid before the vehicle is released.",
  ]],
  ["Test Drives", [
    "By leaving the vehicle for diagnosis or repair, the customer authorizes Ascencio Auto Repair and its staff to drive the vehicle when reasonably necessary for diagnosis, symptom verification, relearn procedures, or repair verification.",
  ]],
  ["Unrelated Damage or Problems", [
    "Ascencio Auto Repair is responsible only for the work specifically authorized and performed by the shop.",
    "The presence of a new fault after a repair does not automatically mean that fault was caused by the work performed.",
    "Any claim will be inspected and evaluated to determine its cause.",
  ]],
  ["Right to Refuse Work", [
    "Ascencio Auto Repair reserves the right to refuse work or vehicles when there are unsafe conditions, improper modifications, problems outside our services, lack of availability, or other circumstances that prevent performing the work in a professional and safe manner.",
  ]],
  ["Communication", [
    "It is the customer's responsibility to provide a valid phone number and/or email address and to maintain a reasonable means of communication while the vehicle is at our facility.",
  ]],
  ["Acceptance", [
    "By requesting service, authorizing a repair, approving an estimate, or leaving a vehicle at Ascencio Auto Repair, the customer acknowledges having had access to these Terms & Conditions and accepts the policies applicable to the authorized service.",
  ]],
];

const ADDENDUM = [
  "Once the customer has been notified that their vehicle is finished, ready for pickup, or that no additional work will be performed, it is the customer's responsibility to pick up the vehicle and settle any outstanding balance.",
  "If the vehicle remains at Ascencio Auto Repair's premises for 30 days or more without being picked up, it may be considered an unclaimed vehicle and Ascencio Auto Repair may initiate the corresponding legal procedures permitted by the laws of the State of Oklahoma.",
  "This may include applicable storage charges and any legal procedure available to recover amounts owed and manage an unclaimed vehicle.",
  "Any action related to an unclaimed vehicle will be carried out in accordance with the notices, deadlines and procedures required by applicable Oklahoma law.",
  "Abandoning the vehicle does not eliminate the customer's responsibility for authorized repairs, diagnostics, storage, or other legitimately owed charges.",
];

export default function Terms() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white px-6 md:px-12 lg:px-24 py-24" data-testid="terms-page">
      <div className="max-w-3xl">
        <Link to="/" className="inline-flex items-center gap-2 text-zinc-400 hover:text-[#FF3B30] text-sm uppercase tracking-[0.15em] transition-colors duration-200" data-testid="terms-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>
        <img src="/logo.png" alt="Ascencio Auto Repair — official logo" className="h-16 w-auto mt-10" draggable="false" data-testid="terms-logo" />
        <h1 className="mt-8 font-oswald font-bold uppercase tracking-tight text-4xl sm:text-5xl">Terms &amp; Conditions of Service</h1>
        <p className="mt-2 text-zinc-500 text-sm">Last updated: August 2026</p>
        <p className="mt-8 text-zinc-300 text-sm md:text-base leading-relaxed border-l-2 border-[#FF3B30] pl-4" data-testid="terms-intro">
          By authorizing work or leaving a vehicle at Ascencio Auto Repair, the customer acknowledges
          and agrees to the following terms and conditions.
        </p>

        <div className="mt-10 space-y-10 text-zinc-300 text-sm md:text-base leading-relaxed">
          {SECTIONS.map(([title, paras], i) => (
            <section key={title} data-testid={`terms-section-${i + 1}`}>
              <h2 className="font-oswald uppercase tracking-wide text-xl text-white mb-3">
                <span className="text-[#FF3B30] mr-2">{i + 1}.</span>{title}
              </h2>
              <div className="space-y-3">
                {paras.map((p, j) => <p key={j}>{p}</p>)}
              </div>
            </section>
          ))}

          <section className="border border-zinc-800 bg-zinc-900/40 p-6 md:p-8" data-testid="terms-addendum">
            <h2 className="font-oswald uppercase tracking-wide text-xl text-white mb-3">
              Unclaimed Vehicles <span className="text-[#FF3B30]">— 30 Days</span>
            </h2>
            <div className="space-y-3">
              {ADDENDUM.map((p, j) => <p key={j}>{p}</p>)}
            </div>
          </section>

          <section>
            <h2 className="font-oswald uppercase tracking-wide text-xl text-white mb-3">Contact</h2>
            <p>
              {BUSINESS.name} · {BUSINESS.street}, {BUSINESS.city} · {BUSINESS.phone} ·{" "}
              <a href={`mailto:${BUSINESS.email}`} className="text-[#FF3B30] hover:underline">{BUSINESS.email}</a>
            </p>
            <p className="mt-4 font-oswald uppercase tracking-wide text-[#F5C518] text-sm">"{BUSINESS.tagline}"</p>
          </section>
        </div>
      </div>
    </div>
  );
}
